import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Plus, Undo2 } from 'lucide-react'
import { copy } from '../../copy/investor-copy'
import { formatShortDate } from '../following/dates'
import { PrivateTag, RelCard } from './rel-card'
import type { CardActions, CardVariant, Relationship } from './types'

const OUTCOME_COPY = {
  NOT_A_FIT: 'pass.outcome.not_a_fit',
  TOO_EARLY: 'pass.outcome.too_early',
  NOT_DEPLOYING: 'pass.outcome.not_deploying',
} as const

/** The reason in words: the outcome's label, or the investor's own text when it was left unspecified. */
export function passReason(decision: NonNullable<Relationship['decision']>): string {
  const key = OUTCOME_COPY[decision.outcome as keyof typeof OUTCOME_COPY]
  if (key) return copy(key)
  return decision.reason ?? copy('pass.outcome.none')
}

/** "You passed on {date}": private to the investor, with the way back. */
export function PassedCard({
  data,
  variant,
  actions,
}: {
  data: Relationship
  variant: CardVariant
  actions: CardActions
}) {
  const { decision } = data
  if (!decision) return null
  return (
    <RelCard
      title={copy('pass.title', { date: formatShortDate(decision.createdAt) })}
      right={<PrivateTag label={copy('ai.private')} />}
    >
      <p className="mt-2 text-sm text-foreground/90">
        <span className="text-muted-foreground">{copy('pass.reason')} </span>
        {passReason(decision)}
      </p>
      {decision.revisitWhen && (
        <p className="mt-1 text-sm text-foreground/90">
          <span className="text-muted-foreground">{copy('pass.revisit')} </span>
          {decision.revisitWhen}
        </p>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <SecondaryButton size="sm" icon={<Undo2 />} onClick={actions.onUndoPass}>
          {copy('pass.undo')}
        </SecondaryButton>
        {variant === 'panel' && (
          <SecondaryButton
            size="sm"
            icon={<Plus />}
            onClick={(e) => actions.onFollow(undefined, e.currentTarget)}
          >
            {copy('ai.follow')}
          </SecondaryButton>
        )}
      </div>
    </RelCard>
  )
}
